import { supabase } from "./supabase";

/**
 * Owner Service - Manages owner-specific functionality
 *
 * This service handles:
 * - Promoting users to owner (one-time only)
 * - Managing admin privileges (add/remove)
 * - Viewing all admins
 *
 * SECURITY: All functions verify owner permissions server-side
 */

// Cache for owner data with 2-minute expiration (shorter than other caches for security)
const cache = new Map();
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log(`📋 Cache hit for owner data: ${key}`);
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
  console.log(`💾 Cached owner data for ${key}`);
};

export const ownerService = {
  /**
   * Check if current user is the owner
   */
  async isOwner(userId) {
    try {
      console.log(`🔍 Checking if user ${userId} is owner`);

      const cacheKey = `is_owner_${userId}`;
      const cachedResult = getCachedData(cacheKey);
      if (cachedResult !== null) return cachedResult;

      const { data, error } = await supabase
        .from("profiles")
        .select("is_owner")
        .eq("id", userId)
        .eq("is_owner", true)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("❌ Error checking owner status:", error);
        throw error;
      }

      const isOwnerResult = !!data;
      setCachedData(cacheKey, isOwnerResult);

      console.log(`✅ Owner check result for ${userId}:`, isOwnerResult);
      return isOwnerResult;
    } catch (error) {
      console.error("❌ Error in isOwner:", error);
      return false;
    }
  },

  /**
   * Get current owner information
   */
  async getOwnerInfo() {
    try {
      console.log("🔍 Getting owner information");

      const cacheKey = "owner_info";
      const cachedResult = getCachedData(cacheKey);
      if (cachedResult) return cachedResult;

      const { data, error } = await supabase
        .from("profiles")
        .select("id, username, email, created_at")
        .eq("is_owner", true)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("❌ Error getting owner info:", error);
        throw error;
      }

      if (!data) {
        console.log("ℹ️ No owner exists yet");
        setCachedData(cacheKey, null);
        return null;
      }

      console.log(`✅ Owner found: ${data.username}`);
      setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      console.error("❌ Error in getOwnerInfo:", error);
      return null;
    }
  },

  /**
   * Promote a user to owner (can only be done once)
   */
  async promoteToOwner(targetUserId) {
    try {
      console.log(`👑 Promoting user ${targetUserId} to owner`);

      const { data, error } = await supabase.rpc("promote_to_owner", {
        target_user_id: targetUserId,
      });

      if (error) {
        console.error("❌ Error promoting to owner:", error);
        throw error;
      }

      console.log("✅ Promotion result:", data);

      // Clear cache since owner status has changed
      cache.clear();

      return {
        success: data.success,
        message: data.message,
        error: data.error,
        ownerUsername: data.owner_username,
        ownerEmail: data.owner_email,
      };
    } catch (error) {
      console.error("❌ Error in promoteToOwner:", error);
      throw error;
    }
  },

  /**
   * Add admin privileges to a user (owner only)
   */
  async addAdmin(ownerUserId, targetUserId = null, targetUsername = null) {
    try {
      console.log(`➕ Adding admin privileges`, {
        ownerUserId,
        targetUserId,
        targetUsername,
      });

      const { data, error } = await supabase.rpc("owner_manage_admin", {
        owner_user_id: ownerUserId,
        target_user_id: targetUserId,
        action: "add",
        target_username: targetUsername,
      });

      if (error) {
        console.error("❌ Error adding admin:", error);
        throw error;
      }

      console.log("✅ Add admin result:", data);

      // Clear admin cache
      cache.delete("all_admins");

      return {
        success: data.success,
        message: data.message,
        error: data.error,
        adminUsername: data.admin_username,
        adminEmail: data.admin_email,
      };
    } catch (error) {
      console.error("❌ Error in addAdmin:", error);
      throw error;
    }
  },

  /**
   * Remove admin privileges from a user (owner only)
   */
  async removeAdmin(ownerUserId, targetUserId = null, targetUsername = null) {
    try {
      console.log(`➖ Removing admin privileges`, {
        ownerUserId,
        targetUserId,
        targetUsername,
      });

      const { data, error } = await supabase.rpc("owner_manage_admin", {
        owner_user_id: ownerUserId,
        target_user_id: targetUserId,
        action: "remove",
        target_username: targetUsername,
      });

      if (error) {
        console.error("❌ Error removing admin:", error);
        throw error;
      }

      console.log("✅ Remove admin result:", data);

      // Clear admin cache
      cache.delete("all_admins");

      return {
        success: data.success,
        message: data.message,
        error: data.error,
        formerAdminUsername: data.former_admin_username,
        formerAdminEmail: data.former_admin_email,
      };
    } catch (error) {
      console.error("❌ Error in removeAdmin:", error);
      throw error;
    }
  },

  /**
   * Get all admins and owner (owner only)
   */
  async getAllAdmins(ownerUserId) {
    try {
      console.log(`📋 Getting all admins for owner ${ownerUserId}`);

      const cacheKey = "all_admins";
      const cachedResult = getCachedData(cacheKey);
      if (cachedResult) return cachedResult;

      const { data, error } = await supabase.rpc("get_all_admins", {
        owner_user_id: ownerUserId,
      });

      if (error) {
        console.error("❌ Error getting all admins:", error);
        throw error;
      }

      console.log(`✅ Admin data received:`, data);

      // The function now returns a JSONB object with the structure we need
      const result = {
        owner: data.owner,
        admins: data.admins,
        totalCount: data.totalCount,
        adminCount: data.adminCount,
      };

      console.log(`✅ Processed admin data:`, result);

      setCachedData(cacheKey, result);
      return result;
    } catch (error) {
      console.error("❌ Error in getAllAdmins:", error);
      throw error;
    }
  },

  /**
   * Search users by username or email (for adding admins)
   */
  async searchUsers(ownerUserId, searchTerm, limit = 10) {
    try {
      console.log(`🔍 Searching users for term: ${searchTerm}`);

      // First verify the caller is owner
      const isOwnerResult = await this.isOwner(ownerUserId);
      if (!isOwnerResult) {
        throw new Error("Only the owner can search for users");
      }

      const { data, error } = await supabase
        .from("profiles")
        .select(
          "id, username, email, is_admin, is_owner, user_type, created_at"
        )
        .or(`username.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
        .eq("is_owner", false) // Don't include owner in search results
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) {
        console.error("❌ Error searching users:", error);
        throw error;
      }

      console.log(`✅ Found ${data.length} users matching "${searchTerm}"`);
      return data;
    } catch (error) {
      console.error("❌ Error in searchUsers:", error);
      throw error;
    }
  },

  /**
   * Clear cache (useful for testing or after major changes)
   */
  clearCache() {
    cache.clear();
    console.log("🧹 Owner service cache cleared");
  },

  /**
   * Get cache stats (for debugging)
   */
  getCacheStats() {
    return {
      size: cache.size,
      keys: Array.from(cache.keys()),
      duration: CACHE_DURATION,
    };
  },
};
